import { useEffect } from "react";

/**
 * Hook personalizado para manejar atajos de teclado
 *
 * @param {string} keyCombo - Tecla o combinación de teclas:
 *   - Una sola tecla: "a", "A", "enter" → se convierte automáticamente en Ctrl + Shift + [tecla]
 *   - Combinación completa: "ctrl+alt+a", "cmd+shift+s", etc.
 *   - Es insensible a mayúsculas/minúsculas: "a" y "A" son equivalentes
 *
 * @param {function} action - Función a ejecutar cuando se detecte el atajo
 *
 * @example
 * // MODO SIMPLE (Ctrl + Shift automático):
 * useKeyPress("a", createProduct);           // = Ctrl + Shift + A
 * useKeyPress("s", saveData);                // = Ctrl + Shift + S
 * useKeyPress("enter", submitForm);          // = Ctrl + Shift + Enter
 * useKeyPress("escape", closeModal);         // = Ctrl + Shift + Escape
 *
 * @example
 * // COMBINACIONES PERSONALIZADAS:
 * useKeyPress("ctrl+a", selectAll);          // = Ctrl + A (seleccionar todo)
 * useKeyPress("ctrl+s", save);               // = Ctrl + S (guardar)
 * useKeyPress("alt+f4", closeApp);           // = Alt + F4
 * useKeyPress("ctrl+alt+delete", restart);   // = Ctrl + Alt + Delete
 * useKeyPress("shift+enter", newLine);       // = Shift + Enter
 *
 * @example
 * // PARA macOS (usando cmd):
 * useKeyPress("cmd+a", selectAll);           // = Cmd + A
 * useKeyPress("cmd+c", copy);                // = Cmd + C
 * useKeyPress("cmd+shift+3", screenshot);    // = Cmd + Shift + 3
 *
 * @example
 * // TECLAS ESPECIALES:
 * useKeyPress("f1", showHelp);               // = Ctrl + Shift + F1
 * useKeyPress("ctrl+f5", refresh);           // = Ctrl + F5
 * useKeyPress("space", playPause);           // = Ctrl + Shift + Space
 *
 * @example
 * // NÚMEROS:
 * useKeyPress("1", goToTab1);                // = Ctrl + Shift + 1
 * useKeyPress("ctrl+1", quickAction1);       // = Ctrl + 1
 *
 * @example
 * // CASOS INSENSIBLES A MAYÚSCULAS:
 * useKeyPress("a", action);                  // Funciona con 'a' y 'A'
 * useKeyPress("A", action);                  // Mismo resultado que arriba
 * useKeyPress("CTRL+SHIFT+A", action);       // Funciona en mayúsculas
 * useKeyPress("ctrl+shift+a", action);       // Mismo resultado que arriba
 *
 * @notes
 * - El hook previene el comportamiento por defecto del navegador
 * - Compatible con Windows, macOS y Linux
 * - Los modificadores deben coincidir exactamente (no se activa con modificadores extra)
 * - Se ejecuta solo en el evento 'keydown'
 */

export const useKeyPress = (keyCombo, action) => {
  useEffect(() => {
    // Validaciones defensivas
    if (!keyCombo || typeof keyCombo !== 'string' || keyCombo.trim() === '') {
      console.warn('useKeyPress: keyCombo debe ser una cadena válida no vacía');
      return;
    }

    if (!action || typeof action !== 'function') {
      console.warn('useKeyPress: action debe ser una función válida');
      return;
    }

    const handleKeyPress = (event) => {
      // Validar que el evento existe y tiene las propiedades necesarias
      if (!event || typeof event !== 'object') {
        return;
      }

      const { ctrlKey, altKey, shiftKey, metaKey, key } = event;

      // Validar que 'key' existe en el evento
      if (!key || typeof key !== 'string' || key.trim() === '') {
        return;
      }

      // Si keyCombo es una sola tecla, usar Ctrl + Shift por defecto
      let combo, expectedKey, expectedModifiers;

      if (keyCombo.includes("+")) {
        // Combinación personalizada (formato completo)
        combo = keyCombo.toLowerCase().trim().split("+");
        expectedKey = combo[combo.length - 1].trim();
        expectedModifiers = combo.slice(0, -1).map((mod) => mod.trim());
      } else {
        // Solo una tecla = Ctrl + Shift + tecla por defecto
        expectedKey = keyCombo.toLowerCase().trim();
        expectedModifiers = ["ctrl", "shift"];
      }

      // Normalizar la tecla presionada (insensible a mayúsculas/minúsculas)
      const pressedKey = key.toLowerCase();

      // Verificar si la tecla presionada coincide
      if (pressedKey !== expectedKey) {
        return;
      }

      // Verificar modificadores
      const hasCtrl = expectedModifiers.includes("ctrl");
      const hasAlt = expectedModifiers.includes("alt");
      const hasShift = expectedModifiers.includes("shift");
      const hasCmd =
        expectedModifiers.includes("cmd") || expectedModifiers.includes("meta");

      // En macOS, cmd también puede ser ctrl en algunos casos
      const ctrlOrCmd = ctrlKey || (hasCmd && metaKey);

      // Verificar que los modificadores coincidan exactamente
      if (
        (hasCtrl ? ctrlOrCmd : !ctrlKey) &&
        (hasAlt ? altKey : !altKey) &&
        (hasShift ? shiftKey : !shiftKey) &&
        (hasCmd ? metaKey : !metaKey)
      ) {
        event.preventDefault();
        try {
          action();
        } catch (error) {
          console.error('Error en useKeyPress al ejecutar action:', error);
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [keyCombo, action]);
};
